// App entry point - Simplified VLE Plus (Billing Only)
import { useState, useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import { LanguageProvider } from "@/lib/i18n/LanguageContext";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { HelmetProvider } from "react-helmet-async";
import { DashboardSkeleton } from "@/components/ui/LoadingSkeleton";
import { OfflineIndicator } from "@/components/layout/OfflineIndicator";
import { ToolRouteGuard } from "@/components/guards/ToolRouteGuard";
import { VLE_TOOLS } from "@/hooks/useVLETools";

// Critical pages - load immediately
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
import AuthBridge from "./pages/AuthBridge";

// Preload functions for route prefetching
const preloadSales = () => import("./pages/Sales");
const preloadDailyBook = () => import("./pages/DailyBook");
const preloadServices = () => import("./pages/Services");
const preloadCustomers = () => import("./pages/Customers");
const preloadSettings = () => import("./pages/Settings");

// Lazy load heavy pages with prefetch support
const Sales = lazy(preloadSales);
const DailyBook = lazy(preloadDailyBook);
const Services = lazy(preloadServices);
const KioskBook = lazy(() => import("./pages/KioskBook"));
const KioskReport = lazy(() => import("./pages/KioskReport"));
const PikVima = lazy(() => import("./pages/PikVima"));
const PikVimaSettings = lazy(() => import("./pages/PikVimaSettings"));
const PikVimaCustomerProfile = lazy(() => import("./pages/PikVimaCustomerProfile"));
const DocumentSlip = lazy(() => import("./pages/DocumentSlip"));
const SetuSuvidhaDocSlip = lazy(() => import("./pages/SetuSuvidhaDocSlip"));
const CRMCenter = lazy(() => import("./pages/CRMCenter"));
const MahasarathiCRM = lazy(() => import("./pages/MahasarathiCRM"));
const PanCardCRM = lazy(() => import("./pages/PanCardCRM"));
const VoterIdCRM = lazy(() => import("./pages/VoterIdCRM"));
const BandhkamKamgarCRM = lazy(() => import("./pages/BandhkamKamgarCRM"));
const MahasarathiSettings = lazy(() => import("./pages/MahasarathiSettings"));

const Expenses = lazy(() => import("./pages/Expenses"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(preloadSettings);
const Customers = lazy(preloadCustomers);
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const FindCSC = lazy(() => import("./pages/FindCSC"));
const VLEPublicProfile = lazy(() => import("./pages/VLEPublicProfile"));

// Compact loading spinner
const PageLoader = () => (
  <div className="flex h-screen items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
      <span className="text-sm text-muted-foreground">Loading...</span>
    </div>
  </div>
);

// Preload common routes when Dashboard loads
const usePreloadRoutes = () => {
  useEffect(() => {
    // Preload most used routes after initial render
    const timer = setTimeout(() => {
      preloadSales();
      preloadServices();
      preloadCustomers();
      preloadDailyBook();
      preloadSettings();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
};

// SPA redirect handler for hosting platforms that use 404.html redirect
const SPARedirectHandler = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    
    if (redirect) {
      const decodedPath = decodeURIComponent(redirect);
      console.log('[SPARedirect] Redirecting to:', decodedPath);
      
      // Clean the URL and navigate using React Router
      window.history.replaceState(null, '', decodedPath);
      navigate(decodedPath, { replace: true });
    }
  }, [navigate]);
  
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        if (error?.status === 401 || error?.message?.includes('JWT')) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 1000 * 60 * 5,
    },
  },
});

// Protected route for authenticated users
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isBillingInProgress, refreshSession, isSessionReady, profile, tenant, roles } = useAuth();
  const [isRecovering, setIsRecovering] = useState(false);
  
  useEffect(() => {
    if (!user && !isLoading && isBillingInProgress && !isRecovering) {
      console.log('[ProtectedRoute] No user during billing, attempting recovery...');
      setIsRecovering(true);
      refreshSession().finally(() => setIsRecovering(false));
    }
  }, [user, isLoading, isBillingInProgress, refreshSession, isRecovering]);
  
  if (isLoading || isRecovering) return (
    <div className="flex h-screen items-center justify-center p-6 bg-background">
      <DashboardSkeleton />
    </div>
  );
  
  if (!user && isBillingInProgress) {
    return (
      <div className="flex h-screen items-center justify-center p-6 bg-background">
        <div className="text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Reconnecting session...</p>
        </div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/auth-bridge" replace />;
  
  // Wait for session to be fully ready
  if (!isSessionReady) {
    return (
      <div className="flex h-screen items-center justify-center p-6 bg-background">
        <div className="text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your workspace...</p>
          <p className="text-xs text-muted-foreground mt-2">
            {!profile ? 'Loading profile...' : !tenant ? 'Loading business...' : !roles.length ? 'Loading permissions...' : 'Almost ready...'}
          </p>
        </div>
      </div>
    );
  }
  
  return <>{children}</>;
}

const AppRoutes = () => {
  // Preload routes after dashboard loads
  usePreloadRoutes();
  
  return (
  <Suspense fallback={<PageLoader />}>
    <SPARedirectHandler />
    <Routes>
      {/* Auth Bridge - Entry point from SETU Suvidha */}
      <Route path="/auth-bridge" element={<AuthBridge />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* Public CSC Directory Routes */}
      <Route path="/find-csc" element={<FindCSC />} />
      <Route path="/csc/:slug" element={<VLEPublicProfile />} />
      <Route path="/csc/:state/:district/:taluka/:village/:businessSlug" element={<VLEPublicProfile />} />
      
      {/* Protected Billing Routes - Core routes always accessible */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/sales" element={<ProtectedRoute><Sales /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute><Services /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      
      {/* Tool-Protected Routes - Only accessible if tool is enabled */}
      <Route path="/daily-book" element={
        <ProtectedRoute>
          <ToolRouteGuard toolKey={VLE_TOOLS.DAILY_BOOK}>
            <DailyBook />
          </ToolRouteGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/kiosk-book" element={
        <ProtectedRoute>
          <ToolRouteGuard toolKey={VLE_TOOLS.KIOSK_BANKING}>
            <KioskBook />
          </ToolRouteGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/kiosk-report" element={
        <ProtectedRoute>
          <ToolRouteGuard toolKey={VLE_TOOLS.KIOSK_BANKING}>
            <KioskReport />
          </ToolRouteGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/pik-vima" element={
        <ProtectedRoute>
          <ToolRouteGuard toolKey={VLE_TOOLS.PIK_VIMA}>
            <PikVima />
          </ToolRouteGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/pik-vima-settings" element={
        <ProtectedRoute>
          <ToolRouteGuard toolKey={VLE_TOOLS.PIK_VIMA}>
            <PikVimaSettings />
          </ToolRouteGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/pik-vima/customer/:id" element={
        <ProtectedRoute>
          <ToolRouteGuard toolKey={VLE_TOOLS.PIK_VIMA}>
            <PikVimaCustomerProfile />
          </ToolRouteGuard>
        </ProtectedRoute>
      } />

      {/* Document Slip - Full Management */}
      <Route path="/document-slip" element={
        <ProtectedRoute>
          <DocumentSlip />
        </ProtectedRoute>
      } />
      
      {/* Setu Suvidha Doc Slip - Customer-facing print only */}
      <Route path="/setu-suvidha/doc-slip" element={
        <ProtectedRoute>
          <SetuSuvidhaDocSlip />
        </ProtectedRoute>
      } />
      
      {/* CRM Center */}
      <Route path="/crm-center" element={
        <ProtectedRoute>
          <CRMCenter />
        </ProtectedRoute>
      } />
      
      {/* Mahasarathi CRM */}
      <Route path="/mahasarathi" element={
        <ProtectedRoute>
          <MahasarathiCRM />
        </ProtectedRoute>
      } />
      {/* Pan Card CRM */}
      <Route path="/pan-card" element={
        <ProtectedRoute>
          <PanCardCRM />
        </ProtectedRoute>
      } />
      {/* Voter ID CRM */}
      <Route path="/voter-id" element={
        <ProtectedRoute>
          <VoterIdCRM />
        </ProtectedRoute>
      } />
      {/* Bandhkam Kamgar CRM */}
      <Route path="/bandhkam-kamgar" element={
        <ProtectedRoute>
          <BandhkamKamgarCRM />
        </ProtectedRoute>
      } />
      <Route path="/mahasarathi-settings" element={
        <ProtectedRoute>
          <MahasarathiSettings />
        </ProtectedRoute>
      } />
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light">
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AuthProvider>
                <AppRoutes />
                <OfflineIndicator />
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
